import { Modal } from "antd";
import classNames from "classnames";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { NAME_SPACES } from "shared/locales/constants";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Button from "../Components/Button";
import CustomModal from "../Components/CustomModal";
import Head from "../Components/Head";
import Label from "../Components/Label";
import Layout from "../Components/Layout";
import Radio from "../Components/Radio";
import checkMarkSmall from "../Components/svg/check_small-secondary.svg";
import euroIcon from "../Components/svg/euro-icon.svg";
import validation from "./validation";

const formulas = [
  {
    label: "ECONOMIC",
    value: 1,
  },
  {
    label: "ESSENTIAL",
    value: 2,
  },
  {
    label: "COMFORT",
    value: 3,
  },
  {
    label: "OPTIMAL",
    value: 4,
  },
];
const custom = [{ label: "CUSTOM", value: null }];

const budgetIntervals = [
  {
    key: "LESS_THAN_50",
    value: { min: 0, max: 50 },
  },
  {
    key: "FROM_50_TO_100",
    value: { min: 50, max: 100 },
  },
  {
    key: "FROM_100_TO_200",
    value: { min: 100, max: 200 },
  },
  {
    key: "FROM_200_TO_500",
    value: { min: 200, max: 500 },
  },
  {
    key: "MORE_THAN_500",
    value: { min: 500, max: 1000 },
  },
];

const View = ({ fields, onNext, onBack }) => {
  const [showBudget, setShowBudget] = useState(
    !(get(fields, "budget.min") === 0 && get(fields, "budget.max") === 1000),
  );
  const [open, setOpen] = useState(false);
  const [selectedFormulaHelp, setSelectedFormulaHelp] = useState(null);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_LMF.COLLECTION_OF_NEEDS", { returnObjects: true });

  // Find matching budget interval from initial fields
  const getInitialBudget = () => {
    const initialMin = get(fields, "budget.min");
    const initialMax = get(fields, "budget.max");

    if (initialMin !== undefined && initialMax !== undefined) {
      const matchingInterval = budgetIntervals.find(
        ({ value }) => value.min === initialMin && value.max === initialMax,
      );
      return matchingInterval ? matchingInterval.value : fields.budget;
    }

    return fields.budget;
  };

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      ...fields,
      budget: getInitialBudget(),
    },
    onSubmit: (values) => {
      const budget = values.budget || { min: 0, max: 1000 };
      onNext({ ...values, budget });
    },
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        width={400}
        footer={null}
        centered
        className="LMF-formula-modal"
      >
        <>
          <span className="LMF-formula-modal__title">
            {TRANSLATION.MODAL.FORMULA} "
            {
              TRANSLATION.FORMULAS[
                formulas.find(({ value }) => value === selectedFormulaHelp)
                  ?.label
              ]?.LABEL
            }
            "
          </span>
          <div className="LMF-formula-modal__content">
            <ul>
              {(
                TRANSLATION.FORMULAS[
                  formulas.find(({ value }) => value === selectedFormulaHelp)
                    ?.label
                ]?.ADVANTAGES || []
              ).map((item, index) => (
                <li key={index}>
                  <img src={checkMarkSmall} alt="✔" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <Button
            onClick={() => setOpen(false)}
            className="w-full"
            type="white"
            size="middle"
          >
            <span>{TRANSLATION.CLOSE}</span>
          </Button>
        </>
      </Modal>
      <CustomModal
        open={open}
        setOpen={setOpen}
        title={
          <>
            {TRANSLATION.MODAL.FORMULA} "
            {
              TRANSLATION.FORMULAS[
                formulas.find(({ value }) => value === selectedFormulaHelp)
                  ?.label
              ]?.LABEL
            }
            "
          </>
        }
        width={420}
        TRANSLATION={TRANSLATION}
        closeText={TRANSLATION.MODAL.CLOSE}
      >
        <ul className="lmf__list">
          {(
            TRANSLATION.FORMULAS[
              formulas.find(({ value }) => value === selectedFormulaHelp)?.label
            ]?.ADVANTAGES || []
          ).map((item, index) => (
            <li key={index}>
              <img src={checkMarkSmall} alt="✔" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </CustomModal>

      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />
        <div className="__LMF--content LMF-content lmf__form grid--1">
          <Radio
            type="list"
            disabled={false}
            values={formulas.map(({ value, label }) => ({
              label: TRANSLATION.FORMULAS[label].LABEL,
              value,
              subLabel: TRANSLATION.FORMULAS[label].SUB_LABEL,
              onHelpClick: (v) => {
                setSelectedFormulaHelp(v);
                setOpen(true);
              },
            }))}
            {...bindInputProps({
              name: "selectedLevel",
              values,
              setFieldValue,
              ...formProps,
            })}
            onChange={async (value) => {
              if (!value) return setShowBudget(true);
              setFieldValue("selectedLevel", value);
              await formProps.validateForm();
              formProps.setFieldTouched(`selectedLevel`);
            }}
            errors={[]}
          />
          <Radio
            type="list"
            values={custom.map(({ value, label }) => ({
              label: TRANSLATION.FORMULAS[label].LABEL,
              value,
            }))}
            onChange={() => setShowBudget(true)}
          />
          {showBudget && (
            <div className="lmfCustom__budget">
              <Label
                label={TRANSLATION.FORM.BUDGET.LABEL}
                icon={<img src={euroIcon} alt="" />}
              />
              <Radio
                type="list"
                orientation="row"
                disabled={false}
                values={budgetIntervals.map(({ key, value }) => ({
                  label: TRANSLATION.BUDGET_INTERVALS[key],
                  value: JSON.stringify(value),
                }))}
                {...bindInputProps({
                  name: "budget",
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                value={
                  values.budget ? JSON.stringify(values.budget) : undefined
                }
                onChange={async (value) => {
                  if (!value) {
                    setFieldValue("budget", null);
                  } else {
                    setFieldValue("budget", JSON.parse(value));
                  }
                  await formProps.validateForm();
                  formProps.setFieldTouched("budget");
                }}
                errors={[]}
              />
            </div>
          )}
        </div>

        <ActionButtons
          disabled={!isEmpty(formProps.errors) || !get(values, "selectedLevel")}
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />
      </Layout>
    </>
  );
};

export default View;
